import { supabase } from '@/lib/supabase';

/**
 * Reports Service - Handles all report-related operations with Supabase
 */

export const reportsService = {
  /**
   * Get all reports, optionally sorted
   * @param {string} sortBy - Sort field (e.g., '-created_date' for descending)
   * @returns {Promise<Array>}
   */
  async list(sortBy = '-created_date') {
    const ascending = !sortBy.startsWith('-');
    const field = sortBy.replace('-', '');
    
    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .order(field, { ascending });

    if (error) throw error;
    return data || [];
  },

  /**
   * Filter reports by criteria
   * @param {Object} filters - Filter criteria (e.g., { created_by: 'email', status: 'reported' })
   * @param {string} sortBy - Sort field
   * @returns {Promise<Array>}
   */
  async filter(filters = {}, sortBy = '-created_date') {
    const ascending = !sortBy.startsWith('-');
    const field = sortBy.replace('-', '');
    
    let query = supabase.from('reports').select('*');

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });

    // Apply sorting
    query = query.order(field, { ascending });

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  /**
   * Get a single report by ID
   * @param {string} id - Report ID
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const { data, error } = await supabase
      .from('reports')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    // Fetch reporter's full name
    if (data?.created_by) {
      const { data: userData } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', data.created_by)
        .single();
      data.reporter_name = userData?.full_name || userData?.email || 'Unknown';
    } else {
      data.reporter_name = 'Unknown';
    }

    return data;
  },

  /**
   * Create a new report
   * @param {Object} reportData - Report data
   * @returns {Promise<Object>}
   */
  async create(reportData) {
    const insertData = {
      ...reportData,
      created_date: new Date().toISOString(),
      upvote_count: 0,
      comment_count: 0,
      status: reportData.status || 'reported'
    };
    
    const { data, error } = await supabase
      .from('reports')
      .insert([insertData])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update a report
   * @param {string} id - Report ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>}
   */
  async update(id, updates) {
    console.log('📝 Updating report:', id, 'with:', updates);
    
    const { data, error } = await supabase
      .from('reports')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('❌ Report update error:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }
    
    console.log('✅ Report updated successfully:', data);
    return data;
  },

  /**
   * Delete a report
   * @param {string} id - Report ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    const { data, error } = await supabase
      .from('reports')
      .delete()
      .eq('id', id)
      .select('id');

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error('Delete failed: report was not removed (permission denied or already deleted).');
    }
  },

  /**
   * Increment upvote count
   * @param {string} id - Report ID
   * @returns {Promise<Object>}
   */
  async incrementUpvotes(id) {
    const report = await this.getById(id);
    return this.update(id, { 
      upvote_count: (report.upvote_count || 0) + 1 
    });
  },

  /**
   * Decrement upvote count
   * @param {string} id - Report ID
   * @returns {Promise<Object>}
   */
  async decrementUpvotes(id) {
    const report = await this.getById(id);
    return this.update(id, { 
      upvote_count: Math.max(0, (report.upvote_count || 0) - 1) 
    });
  },

  /**
   * Increment comment count
   * @param {string} id - Report ID
   * @returns {Promise<Object>}
   */
  async incrementComments(id) {
    const report = await this.getById(id);
    return this.update(id, { 
      comment_count: (report.comment_count || 0) + 1 
    });
  }
};
